import vtkColorMaps from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction/ColorMaps';
import vtkColorTransferFunction from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction';
import vtkPiecewiseFunction from '@kitware/vtk.js/Common/DataModel/PiecewiseFunction';
import isEqual from './isEqual';
import { actorIsA } from './actorCheck';
const _colormaps = new Map();
function normalizeColormapName(name) {
    return name?.trim().toLowerCase();
}
function registerColormap(colormap) {
    colormap.name = colormap.name || colormap.Name;
    _colormaps.set(colormap.name, colormap);
}
function getColormap(name) {
    return _colormaps.get(name);
}
function getColormapNames() {
    return Array.from(_colormaps.keys());
}
function findRegisteredColormap(name) {
    const exactMatch = getColormap(name);
    if (exactMatch) {
        return exactMatch;
    }
    const normalizedName = normalizeColormapName(name);
    if (!normalizedName) {
        return;
    }
    for (const [registeredName, registeredColormap] of _colormaps.entries()) {
        if (normalizeColormapName(registeredName) === normalizedName ||
            normalizeColormapName(registeredColormap.Name) === normalizedName) {
            return registeredColormap;
        }
    }
}
function findVTKColormap(name) {
    const exactMatch = vtkColorMaps.getPresetByName(name);
    if (exactMatch) {
        return exactMatch;
    }
    const normalizedName = normalizeColormapName(name);
    if (!normalizedName) {
        return;
    }
    const matchedPresetName = vtkColorMaps.rgbPresetNames.find((presetName) => normalizeColormapName(presetName) === normalizedName);
    return matchedPresetName
        ? vtkColorMaps.getPresetByName(matchedPresetName)
        : undefined;
}
function resolveColormap(name) {
    if (!name?.trim()) {
        return;
    }
    return findRegisteredColormap(name) || findVTKColormap(name);
}
function findMatchingColormap(rgbPoints, actor) {
    const colormapsVTK = vtkColorMaps.rgbPresetNames.map((presetName) => vtkColorMaps.getPresetByName(presetName));
    const colormapsCS3D = getColormapNames().map((colormapName) => getColormap(colormapName));
    const colormaps = colormapsVTK.concat(colormapsCS3D);
    const matchedColormap = colormaps.find((colormap) => {
        const { RGBPoints: presetRGBPoints } = colormap;
        if (presetRGBPoints.length !== rgbPoints.length) {
            return false;
        }
        for (let i = 0; i < presetRGBPoints.length; i += 4) {
            if (!isEqual(presetRGBPoints.slice(i + 1, i + 4), rgbPoints.slice(i + 1, i + 4))) {
                return false;
            }
        }
        return true;
    });
    if (!matchedColormap) {
        return null;
    }
    const opacity = [];
    if (actorIsA(actor, 'vtkVolume') || actorIsA(actor, 'vtkImageSlice')) {
        const opacityPoints = actor
            .getProperty()
            ?.getScalarOpacity?.(0)
            ?.getDataPointer?.();
        if (!opacityPoints) {
            return {
                name: matchedColormap.Name,
            };
        }
        for (let i = 0; i < opacityPoints.length; i += 2) {
            opacity.push({
                value: opacityPoints[i],
                opacity: opacityPoints[i + 1],
            });
        }
    }
    const result = {
        name: matchedColormap.Name,
        ...(Array.isArray(opacity) && opacity.length > 0 && { opacity }),
        ...(typeof opacity === 'number' && { opacity }),
    };
    return result;
}
export function setColorMapTransferFunctionForVolumeActor(volumeInfo) {
    const { volumeActor, preset, opacity = 0.9, threshold = null, colorRange = [0, 5], } = volumeInfo;
    const mapper = volumeActor.getMapper();
    mapper.setSampleDistance(1.0);
    const cfun = vtkColorTransferFunction.newInstance();
    const presetToUse = preset || vtkColorMaps.getPresetByName('hsv');
    cfun.applyColorMap(presetToUse);
    cfun.setMappingRange(colorRange[0], colorRange[1]);
    volumeActor.getProperty().setRGBTransferFunction(0, cfun);
    updateOpacityWithThreshold(volumeActor, opacity, threshold);
}
export function updateOpacity(volumeActor, newOpacity) {
    const currentThreshold = getThresholdValue(volumeActor);
    updateOpacityWithThreshold(volumeActor, newOpacity, currentThreshold);
}
export function updateThreshold(volumeActor, newThreshold) {
    const currentOpacity = getMaxOpacity(volumeActor);
    updateOpacityWithThreshold(volumeActor, currentOpacity, newThreshold);
}
function updateOpacityWithThreshold(volumeActor, opacity, threshold) {
    const meta = volumeActor.getMapper().getInputData().get('voxelManager');
    if (!meta?.voxelManager) {
        throw new Error('No voxel manager was found for the volume actor, or you cannot yet update opacity with a threshold using stacked images');
    }
    const range = meta.voxelManager.getRange();
    const ofun = vtkPiecewiseFunction.newInstance();
    if (threshold !== null) {
        const delta = Math.abs(range[1] - range[0]) * 0.001;
        const thresholdValue = Math.max(range[0], Math.min(range[1], threshold));
        ofun.addPoint(range[0], 0);
        ofun.addPoint(thresholdValue - delta, 0);
        ofun.addPoint(thresholdValue, opacity);
        ofun.addPoint(range[1], opacity);
    }
    else {
        ofun.addPoint(range[0], opacity);
        ofun.addPoint(range[1], opacity);
    }
    volumeActor.getProperty().setScalarOpacity(0, ofun);
}
function getThresholdValue(volumeActor) {
    const opacityFunction = volumeActor.getProperty().getScalarOpacity(0);
    if (!opacityFunction) {
        return null;
    }
    const dataArray = opacityFunction.getDataPointer();
    if (!dataArray || dataArray.length <= 4) {
        return null;
    }
    for (let i = 0; i < dataArray.length - 2; i += 2) {
        const x1 = dataArray[i];
        const y1 = dataArray[i + 1];
        const x2 = dataArray[i + 2];
        const y2 = dataArray[i + 3];
        if (y1 === 0 && y2 > 0) {
            return x2;
        }
    }
    return null;
}
function getMaxOpacity(volumeActor) {
    const opacityFunction = volumeActor.getProperty().getScalarOpacity(0);
    if (!opacityFunction) {
        return 1.0;
    }
    const dataArray = opacityFunction.getDataPointer();
    if (!dataArray || dataArray.length === 0) {
        return 1.0;
    }
    let maxOpacity = 0;
    for (let i = 1; i < dataArray.length; i += 2) {
        if (dataArray[i] > maxOpacity) {
            maxOpacity = dataArray[i];
        }
    }
    return maxOpacity;
}
export { getColormap, getColormapNames, resolveColormap, registerColormap, findMatchingColormap, getThresholdValue, getMaxOpacity, };
